import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, CalendarOff } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { createAbsence } from "@/Api/absences";
import { getSelectAbsenceTypes } from "@/Api/absenceTypes";
import { getEmployeeVacations } from "@/Api/employeeVacations";
import { useAuth } from "@/hooks/auth";
import { VacationStatusConst } from "@/types/employeeFicha";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileUpload } from "@/App/Distributions/components/FileUpload";
import { AbsenceTypeSelectProps, AbsenceUnitConst } from "@/types/absence";

const schema = z.object({
  absence_type_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
  employee_vacation_id: z.string().optional(),
  start_date: z.string({ message: "*requerido" }).min(1, "*requerido"),
  end_date: z.string().optional(),
  start_time: z.string().optional(),
  end_time: z.string().optional(),
  justification: z
    .string({ message: "*requerido" })
    .min(10, "Mínimo de 10 caracteres"),
});

type SchemaType = z.infer<typeof schema>;

export const RequestAbsenceBtn = ({
  onCreated,
  onlyVacation = false,
}: {
  onCreated?: () => void;
  /** Restringe o formulário ao tipo "Férias" — usado na tela de Planejamento de Férias. */
  onlyVacation?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formState, setFormState] = useState<string>("default");
  const [types, setTypes] = useState<AbsenceTypeSelectProps[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [vacations, setVacations] = useState<any[]>([]);
  const { toast } = useToastNavigate();
  const { user } = useAuth();
  const employeeId = user?.employee?.id;

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      absence_type_id: "",
      employee_vacation_id: "",
      start_date: "",
      end_date: "",
      start_time: "",
      end_time: "",
      justification: "",
    },
  });

  const formWatch = form.watch();
  const selectedType = useMemo(
    () => types.find((type) => String(type.id) === formWatch.absence_type_id),
    [types, formWatch.absence_type_id]
  );
  const isPartial = selectedType?.unit === AbsenceUnitConst.PARTIAL;
  const isVacation = !!selectedType?.is_vacation;

  useEffect(() => {
    if (!isOpen || types.length > 0) return;
    (async () => {
      const { data } = await getSelectAbsenceTypes(
        onlyVacation ? { is_vacation: true } : undefined
      );
      const fetchedTypes = data?.response ?? [];
      setTypes(fetchedTypes);
      if (onlyVacation && fetchedTypes.length > 0) {
        form.setValue("absence_type_id", String(fetchedTypes[0].id));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, types.length, onlyVacation]);

  useEffect(() => {
    if (!isOpen || !isVacation || !employeeId || vacations.length > 0) return;
    (async () => {
      const { data } = await getEmployeeVacations(employeeId);
      const openVacations = (data?.response ?? []).filter(
        (vacation) => vacation.status !== VacationStatusConst.TAKEN
      );
      setVacations(openVacations);
    })();
  }, [isOpen, isVacation, employeeId, vacations.length]);

  const formSubmit = async (data: SchemaType) => {
    if (selectedType?.requires_document && documents.length < 1) {
      toast({
        description: "Esse tipo de abono exige pelo menos um documento anexado.",
        variant: "destructive",
      });
      return;
    }

    setFormState("loading");

    const formData = new FormData();
    formData.append("absence_type_id", data.absence_type_id);
    if (isVacation && data.employee_vacation_id) {
      formData.append("employee_vacation_id", data.employee_vacation_id);
    }
    formData.append("start_date", data.start_date);
    formData.append("end_date", isPartial ? data.start_date : data.end_date || data.start_date);
    if (isPartial) {
      formData.append("start_time", data.start_time || "");
      formData.append("end_time", data.end_time || "");
    }
    formData.append("justification", data.justification);
    documents.forEach((file) => formData.append("documents[]", file));

    const { data: apiResponse, ok } = await createAbsence(formData);
    if (!ok) {
      setFormState("error");
      toast({
        description: apiResponse?.message || "Erro ao solicitar abono",
        variant: "destructive",
      });
      setTimeout(() => setFormState("default"), 2000);
      return;
    }

    setFormState("default");
    setIsOpen(false);
    form.reset();
    setDocuments([]);
    setVacations([]);
    toast({
      description: "Abono solicitado!",
      action: <Check color="#1edc2b" />,
    });
    onCreated?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button variant="outline" className={cn("py-3 flex gap-1", "bg-primary")}>
          <CalendarOff className="size-5" strokeWidth={1.75} />
          {onlyVacation ? "Solicitar Férias" : "Solicitar Abono"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            {onlyVacation ? "Solicitar férias" : "Solicitar abono"}
          </DialogDescription>
        </DialogHeader>
        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-3"
        >
          {!onlyVacation && (
            <Form.Row>
              <Form.Field name="absence_type_id">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Tipo <Form.Message />
                    </Form.Label>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {types.map((type) => (
                          <SelectItem key={type.id} value={String(type.id)}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
          )}

          {isVacation && (
            <Form.Row>
              <Form.Field name="employee_vacation_id">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Vincular a um registro de férias (opcional)
                    </Form.Label>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Nenhum — cria um novo automaticamente" />
                      </SelectTrigger>
                      <SelectContent>
                        {vacations.map((vacation) => (
                          <SelectItem key={vacation.uuid} value={vacation.uuid}>
                            Aquisitivo{" "}
                            {new Date(vacation.acquisition_period_start).toLocaleDateString("pt-BR")}{" "}
                            a{" "}
                            {new Date(vacation.acquisition_period_end).toLocaleDateString("pt-BR")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
          )}

          {isPartial ? (
            <Form.Row>
              <Form.Field name="start_date">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Data <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} type="date" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
              <Form.Field name="start_time">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Início <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} type="time" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
              <Form.Field name="end_time">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Fim <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} type="time" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
          ) : (
            <Form.Row>
              <Form.Field name="start_date">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Data inicial <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} type="date" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
              <Form.Field name="end_date">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>Data final</Form.Label>
                    <Form.Control>
                      <Input {...field} type="date" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
          )}

          <Form.Row>
            <Form.Field name="justification">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Justificativa <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Textarea {...field} placeholder="Explique o motivo do abono" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <FileUpload
            files={documents}
            onFilesChange={setDocuments}
            required={!!selectedType?.requires_document}
            inputId="file-upload-absence"
            showCamera={false}
          />

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Enviando <LoaderSpin />
                </>
              )}
              {formState === "default" && "Enviar solicitação"}
              {formState === "error" && "Erro ao enviar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
